import React, { useContext, useState, useRef, useEffect, useMemo } from 'react';
import { safeRest, defaultBaseline, defaultFonts } from '@wds/utils';
import { createMemoStyles, getColor, BACKGROUND_SEMANTIC_COLORS, BACKGROUND_COLORS as BACKGROUND_COLORS$1 } from '@wds/styles';
import { IconArrowDown, IconArrowUp } from '@wds/icons';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

var __assign = function () {
  __assign = Object.assign || function __assign(t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];
      for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
    }
    return t;
  };
  return __assign.apply(this, arguments);
};
function __rest(s, e) {
  var t = {};
  for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0) t[p] = s[p];
  if (s != null && typeof Object.getOwnPropertySymbols === "function") for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
    if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i])) t[p[i]] = s[p[i]];
  }
  return t;
}
function __spreadArray(to, from, pack) {
  if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
    if (ar || !(i in from)) {
      if (!ar) ar = Array.prototype.slice.call(from, 0, i);
      ar[i] = from[i];
    }
  }
  return to.concat(ar || Array.prototype.slice.call(from));
}
var useStyles$5 = createMemoStyles(function (theme) {
  var palette = theme.palette;
  var shadowStyle = {
    boxShadow: "1px 0 0 ".concat(palette.border.muted, ", 0px 2px 4px rgba(0, 0, 0, 0.14)")
  };
  var removeShadowStyle = {
    boxShadow: "1px 0 0 ".concat(palette.border.muted)
  };
  var tableHeaderStyles = {
    wrenchTableHeader: function (_a) {
      var sticky = _a.sticky,
        elevation = _a.elevation,
        showHeaderShadow = _a.showHeaderShadow,
        shadow = _a.shadow;
      return __assign({
        position: sticky ? 'sticky' : null,
        top: sticky ? 0 : null,
        zIndex: (elevation !== null && elevation !== void 0 ? elevation : sticky) ? 2 : null,
        backgroundColor: sticky ? palette.background.light : null,
        transition: 'all 0.3s'
      }, sticky && shadow && (showHeaderShadow ? shadowStyle : removeShadowStyle));
    }
  };
  return tableHeaderStyles;
});
var TableContext = React.createContext({
  density: 'md',
  showHeaderShadow: false,
  showFooterShadow: false,
  showColumnShadow: false,
  showColumnRightShadow: false
});
var TableHeader = function (_a) {
  var children = _a.children,
    _b = _a.sticky,
    sticky = _b === void 0 ? false : _b,
    _c = _a.shadow,
    shadow = _c === void 0 ? true : _c,
    elevation = _a.elevation,
    rest = __rest(_a, ["children", "sticky", "shadow", "elevation"]);
  var showHeaderShadow = useContext(TableContext).showHeaderShadow;
  var wrenchTableHeader = useStyles$5({
    sticky: sticky,
    elevation: elevation,
    showHeaderShadow: showHeaderShadow,
    shadow: shadow
  }).wrenchTableHeader;
  return React.createElement("thead", __assign({
    className: wrenchTableHeader
  }, safeRest(rest)), children);
};
var TableBody = function (_a) {
  var children = _a.children,
    rest = __rest(_a, ["children"]);
  return React.createElement("tbody", __assign({}, safeRest(rest)), children);
};

/** Resolves the correct color */
var getMarkedColor$1 = function (color, palette) {
  if (!color || typeof color !== 'string') {
    return palette.primary.main;
  }
  if (color.startsWith('data-')) {
    return palette.data[color.replace('data-', '')];
  }
  return palette[color].main;
};
var useStyles$4 = createMemoStyles(function (theme) {
  var palette = theme.palette,
    border = theme.border,
    spacing = theme.spacing;
  var tableRowStyles = {
    wrenchTableRow: function (_a) {
      var color = _a.color,
        disabled = _a.disabled,
        marked = _a.marked,
        selected = _a.selected;
      return {
        opacity: disabled ? '0.5' : null,
        pointerEvents: disabled ? 'none' : null,
        backgroundColor: selected ? palette.background.main : null,
        '&:last-child': {
          '& > td': {
            borderBottom: 'none'
          }
        },
        '& > td': {
          '&:first-child': {
            position: marked ? 'relative' : null,
            '&:before': {
              content: marked ? '""' : null,
              position: marked ? 'absolute' : null,
              left: marked ? '0' : null,
              top: marked ? '0' : null,
              bottom: marked ? '0' : null,
              width: marked ? spacing[1] : null,
              backgroundColor: marked && color ? getMarkedColor$1(color, palette) : 'none',
              borderRight: border.muted
            }
          }
        }
      };
    }
  };
  return tableRowStyles;
});
var TableRow = function (_a) {
  var children = _a.children,
    color = _a.color,
    _b = _a.disabled,
    disabled = _b === void 0 ? false : _b,
    _c = _a.marked,
    marked = _c === void 0 ? false : _c,
    onClick = _a.onClick,
    _d = _a.selected,
    selected = _d === void 0 ? false : _d,
    rest = __rest(_a, ["children", "color", "disabled", "marked", "onClick", "selected"]);
  var wrenchTableRow = useStyles$4({
    color: color,
    disabled: disabled,
    marked: marked,
    selected: selected
  }).wrenchTableRow;
  return React.createElement("tr", __assign({
    onClick: onClick,
    className: wrenchTableRow
  }, safeRest(rest)), children);
};
var getCellPadding$1 = function (spacing, density) {
  switch (density) {
    case 'sm':
      return [spacing[2], spacing[4]];
    case 'lg':
      return [spacing[5], spacing[5]];
    case 'md':
    default:
      return [spacing[4], spacing[4]];
  }
};
var useStyles$3 = createMemoStyles(function (theme) {
  var palette = theme.palette,
    fontSize = theme.type.fontSize,
    spacing = theme.spacing;
  var table = {
    thTypeColor: palette.text.dark,
    thBgColor: palette.transparent.clear,
    bgColor: {
      light: palette.background.light,
      main: palette.background.main
    },
    thDividerLineColor: palette.transparent.clear,
    tdTypeColor: palette.text.dark,
    cellMinHeight: 50,
    fontSize: fontSize.bodySm
  };
  var cellStyles = {
    fontSize: table.fontSize,
    color: table.tdTypeColor,
    minHeight: table.cellMinHeight,
    verticalAlign: 'top',
    boxShadow: "inset 0 -1px 0 0 ".concat(palette.border.muted),
    '& > *': {
      verticalAlign: 'middle'
    }
  };
  var tableStyles = {
    wrenchTableCell: function (_a) {
      var numeric = _a.numeric,
        variant = _a.variant,
        noWrap = _a.noWrap,
        density = _a.density,
        maxWidth = _a.maxWidth,
        minWidth = _a.minWidth,
        align = _a.align,
        backgroundColor = _a.backgroundColor;
      return __assign(__assign(__assign(__assign(__assign(__assign(__assign({}, cellStyles), {
        backgroundColor: table.bgColor[backgroundColor],
        textAlign: numeric ? 'right' : align,
        fontVariantNumeric: numeric ? 'tabular-nums' : null,
        width: variant ? '1%' : null,
        padding: getCellPadding$1(spacing, density)
      }), variant === 'bulkActions' && {
        paddingRight: '0'
      }), {
        whiteSpace: noWrap ? 'nowrap' : null
      }), maxWidth && {
        maxWidth: maxWidth
      }), minWidth && {
        minWidth: minWidth
      }), {
        '& > a': {
          color: palette.text.link
        }
      });
    }
  };
  return tableStyles;
});
var TableCell = function (_a) {
  var children = _a.children,
    _b = _a.numeric,
    numeric = _b === void 0 ? false : _b,
    variant = _a.variant,
    scopeProp = _a.scope,
    parent = _a.parent,
    color = _a.color,
    _c = _a.backgroundColor,
    backgroundColor = _c === void 0 ? 'light' : _c,
    _d = _a.marked,
    marked = _d === void 0 ? false : _d,
    _e = _a.noWrap,
    noWrap = _e === void 0 ? false : _e,
    maxWidth = _a.maxWidth,
    minWidth = _a.minWidth,
    _f = _a.align,
    align = _f === void 0 ? 'left' : _f,
    rest = __rest(_a, ["children", "numeric", "variant", "scope", "parent", "color", "backgroundColor", "marked", "noWrap", "maxWidth", "minWidth", "align"]);
  var density = useContext(TableContext).density;
  var wrenchTableCell = useStyles$3({
    numeric: numeric,
    variant: variant,
    color: color,
    backgroundColor: backgroundColor,
    marked: marked,
    density: density,
    noWrap: noWrap,
    maxWidth: maxWidth,
    minWidth: minWidth,
    align: align
  }).wrenchTableCell;
  var isHeaderChild = parent === 'Table.Header';
  var Component = isHeaderChild ? 'th' : 'td';
  var scope = !scopeProp && isHeaderChild ? 'col' : scopeProp;
  return React.createElement(Component, __assign({
    className: wrenchTableCell,
    scope: scope
  }, safeRest(rest)), children);
};

/** Resolves the correct color */
var getMarkedColor = function (color, palette) {
  if (!color || typeof color !== 'string') {
    return palette.primary.main;
  }
  if (color.startsWith('data-')) {
    return palette.data[color.replace('data-', '')];
  }
  return palette[color].main;
};
var getCellPadding = function (spacing, density, interactive) {
  switch (density) {
    case 'sm':
      return interactive ? [spacing[2], spacing[2]] : [spacing[4], spacing[4]];
    case 'lg':
      return interactive ? [spacing[2], spacing[4]] : [spacing[4], spacing[5]];
    case 'md':
    default:
      return interactive ? spacing[2] : spacing[4];
  }
};
var getFlexAlignment = function (numeric, align) {
  if (numeric) {
    return 'flex-end';
  }
  if (align === 'left') {
    return 'flex-start';
  }
  if (align === 'center') {
    return 'center';
  }
  return 'flex-end';
};
var useStyles$2 = createMemoStyles(function (theme) {
  var palette = theme.palette,
    _a = theme.type,
    fontWeight = _a.fontWeight,
    fontSize = _a.fontSize,
    spacing = theme.spacing;
  var table = {
    thTypeColor: palette.background.dark,
    thBgColor: palette.transparent.clear,
    thDividerLineColor: palette.transparent.clear,
    tdTypeColor: palette.text.dark,
    tdBgColor: palette.transparent.clear,
    cellMinHeight: 50,
    fontSize: fontSize.body
  };
  var cellStyles = {
    fontSize: table.fontSize,
    color: table.tdTypeColor,
    verticalAlign: 'middle',
    boxShadow: "inset 0 -1px 0 0 ".concat(palette.border.muted),
    '& > *': {
      verticalAlign: 'middle'
    }
  };
  var stickyBorder = function (sticky, showColumnShadow, showColumnRightShadow) {
    var shadow = '';
    if (showColumnShadow && sticky === 'left') {
      shadow = '2px 0 4px rgba(0, 0, 0, 0.2)';
    }
    if (showColumnRightShadow && sticky === 'right') {
      shadow = '-2px 0 4px rgba(0, 0, 0, 0.2)';
    }
    return {
      '&:after': {
        content: '""',
        position: 'absolute',
        left: sticky === 'right' ? '0' : null,
        right: sticky === 'left' ? '0' : null,
        top: '0',
        height: '100%',
        width: '1px',
        backgroundColor: palette.border.muted,
        boxShadow: shadow
      }
    };
  };
  var tableStyles = {
    wrenchTableHeaderCell: function (_a) {
      var numeric = _a.numeric,
        noWrap = _a.noWrap,
        maxWidth = _a.maxWidth,
        minWidth = _a.minWidth,
        marked = _a.marked,
        color = _a.color,
        isSortable = _a.isSortable,
        isSorted = _a.isSorted,
        sticky = _a.sticky,
        elevation = _a.elevation,
        backgroundColor = _a.backgroundColor,
        align = _a.align,
        density = _a.density,
        onClick = _a.onClick,
        showColumnShadow = _a.showColumnShadow,
        showColumnRightShadow = _a.showColumnRightShadow,
        shadow = _a.shadow;
      var headerCellPosition = function () {
        if (sticky) {
          return 'sticky';
        }
        if (marked) {
          return 'relative';
        }
        return null;
      };
      return __assign(__assign(__assign(__assign(__assign(__assign(__assign({}, cellStyles), {
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
        padding: getCellPadding(spacing, density, !!(isSortable || onClick)),
        minHeight: table.cellMinHeight,
        textAlign: numeric ? 'right' : align,
        fontWeight: fontWeight.medium,
        backgroundColor: getColor(palette, backgroundColor, {
          colorType: 'background'
        }),
        color: backgroundColor !== 'dark' ? table.thTypeColor : palette.text.light,
        position: headerCellPosition(),
        left: sticky === 'left' ? 0 : null,
        right: sticky === 'right' ? 0 : null,
        userSelect: isSortable ? 'none' : null,
        whiteSpace: noWrap ? 'nowrap' : null,
        zIndex: (elevation !== null && elevation !== void 0 ? elevation : sticky) ? 1 : null,
        transition: 'all 0.3s'
      }), maxWidth && {
        maxWidth: maxWidth
      }), minWidth && {
        minWidth: minWidth
      }), {
        '&:before': {
          content: '""',
          position: 'absolute',
          left: '0',
          top: '0',
          width: '100%',
          height: spacing[1],
          backgroundColor: marked && color ? getMarkedColor(color, palette) : 'none'
        }
      }), sticky && shadow && stickyBorder(sticky, showColumnShadow, showColumnRightShadow)), {
        '&:first-child': {
          borderLeft: 'none'
        },
        '&:hover': {
          '& > button > span:last-child': {
            opacity: 1,
            color: isSorted ? palette.text.dark : palette.text.darkMuted
          }
        }
      });
    },
    wrenchTableSortIcon: function (_a) {
      var isSorted = _a.isSorted;
      return {
        display: 'inline-flex',
        pointerEvents: 'none',
        marginLeft: spacing[2],
        opacity: isSorted ? 1 : 0.42,
        color: palette.text.dark
      };
    },
    wrenchTableHeaderContainer: function (_a) {
      var numeric = _a.numeric,
        align = _a.align;
      return {
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        justifyContent: getFlexAlignment(numeric, align)
      };
    },
    wrenchTableHeaderText: {},
    wrenchTableSortButton: function (_a) {
      var isSorted = _a.isSorted,
        backgroundColor = _a.backgroundColor,
        numeric = _a.numeric,
        density = _a.density,
        align = _a.align;
      return {
        border: 'none',
        background: 'none',
        cursor: 'pointer',
        minHeight: density === 'sm' ? spacing[3] : spacing[6],
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        fontWeight: fontWeight.medium,
        color: backgroundColor !== 'dark' ? table.thTypeColor : palette.text.light,
        justifyContent: getFlexAlignment(numeric, align),
        padding: spacing[2],
        '&:focus': {
          '& > span': {
            opacity: 1,
            color: isSorted ? palette.text.dark : palette.text.darkMuted
          }
        }
      };
    },
    wrenchHeaderCellAddon: {
      marginLeft: spacing[2],
      cursor: 'default',
      fontSize: fontSize.body
    }
  };
  return tableStyles;
});
var SortArrow = function (_a) {
  var sortDirection = _a.sortDirection,
    wrenchTableSortIcon = _a.wrenchTableSortIcon,
    sortAscendingLabel = _a.sortAscendingLabel,
    sortDescendingLabel = _a.sortDescendingLabel;
  var sortIcon;
  if (sortDirection === 'descending') {
    sortIcon = React.createElement(IconArrowDown, {
      title: sortAscendingLabel,
      size: "md"
    });
  } else {
    sortIcon = React.createElement(IconArrowUp, {
      title: sortDescendingLabel,
      size: "md"
    });
  }
  return React.createElement("span", {
    "aria-hidden": "true",
    className: wrenchTableSortIcon
  }, sortIcon);
};
var TableHeaderCell = function (_a) {
  var children = _a.children,
    _b = _a.numeric,
    numeric = _b === void 0 ? false : _b,
    scopeProp = _a.scope,
    parent = _a.parent,
    color = _a.color,
    _c = _a.backgroundColor,
    backgroundColor = _c === void 0 ? 'main' : _c,
    _d = _a.marked,
    marked = _d === void 0 ? false : _d,
    _e = _a.isSortable,
    isSortable = _e === void 0 ? false : _e,
    _f = _a.isSorted,
    isSorted = _f === void 0 ? false : _f,
    _g = _a.sortDirection,
    sortDirection = _g === void 0 ? 'ascending' : _g,
    _h = _a.sortAscendingLabel,
    sortAscendingLabel = _h === void 0 ? 'Sort rows in ascending order.' : _h,
    _j = _a.sortDescendingLabel,
    sortDescendingLabel = _j === void 0 ? 'Sort rows in descending order.' : _j,
    _k = _a.noWrap,
    noWrap = _k === void 0 ? false : _k;
  _a.labelSort;
  var maxWidth = _a.maxWidth,
    minWidth = _a.minWidth,
    sticky = _a.sticky,
    _m = _a.shadow,
    shadow = _m === void 0 ? true : _m,
    elevation = _a.elevation,
    onClick = _a.onClick,
    _o = _a.align,
    align = _o === void 0 ? 'left' : _o,
    interactiveAddon = _a.interactiveAddon,
    rest = __rest(_a, ["children", "numeric", "scope", "parent", "color", "backgroundColor", "marked", "isSortable", "isSorted", "sortDirection", "sortAscendingLabel", "sortDescendingLabel", "noWrap", "labelSort", "maxWidth", "minWidth", "sticky", "shadow", "elevation", "onClick", "align", "interactiveAddon"]);
  var _p = useContext(TableContext),
    density = _p.density,
    showColumnShadow = _p.showColumnShadow,
    showColumnRightShadow = _p.showColumnRightShadow;
  var _q = useStyles$2({
      numeric: numeric,
      noWrap: noWrap,
      maxWidth: maxWidth,
      minWidth: minWidth,
      marked: marked,
      color: color,
      isSortable: isSortable,
      isSorted: isSorted,
      sticky: sticky,
      shadow: shadow,
      backgroundColor: backgroundColor,
      align: align,
      elevation: elevation,
      density: density,
      onClick: onClick,
      showColumnShadow: showColumnShadow,
      showColumnRightShadow: showColumnRightShadow
    }),
    wrenchTableHeaderCell = _q.wrenchTableHeaderCell,
    wrenchTableSortIcon = _q.wrenchTableSortIcon,
    wrenchTableSortButton = _q.wrenchTableSortButton,
    wrenchHeaderCellAddon = _q.wrenchHeaderCellAddon,
    wrenchTableHeaderContainer = _q.wrenchTableHeaderContainer,
    wrenchTableHeaderText = _q.wrenchTableHeaderText;
  var isHeaderChild = parent === 'Table.Header';
  var scope = !scopeProp && isHeaderChild ? 'col' : scopeProp;
  var handleOnClick = function (e) {
    var element = e.target;
    var closest = element.closest('[data-component]');
    if (!closest && onClick) {
      onClick(e);
    }
  };
  var ariaLabel;
  if (isSortable) {
    ariaLabel = sortDirection === 'ascending' ? sortAscendingLabel : sortDescendingLabel;
  }
  return React.createElement("th", __assign({
    className: wrenchTableHeaderCell,
    scope: scope,
    "aria-sort": isSorted ? sortDirection : undefined
  }, safeRest(rest)), isSortable || onClick ? React.createElement("button", {
    type: "button",
    onClick: function (e) {
      return handleOnClick(e);
    },
    "aria-describedby": isSorted ? sortDirection : undefined,
    className: wrenchTableSortButton,
    "data-column": rest['data-column'],
    "aria-label": ariaLabel
  }, React.createElement("span", {
    "data-column": rest['data-column'],
    className: wrenchTableHeaderText
  }, children), interactiveAddon && React.createElement("span", {
    "data-component": "headerCellAddon",
    className: wrenchHeaderCellAddon
  }, interactiveAddon), isSortable && React.createElement(SortArrow, {
    sortDirection: sortDirection,
    wrenchTableSortIcon: wrenchTableSortIcon,
    sortAscendingLabel: sortAscendingLabel,
    sortDescendingLabel: sortDescendingLabel
  })) : React.createElement("div", {
    className: wrenchTableHeaderContainer
  }, React.createElement("span", {
    className: wrenchTableHeaderText
  }, children), interactiveAddon && React.createElement("span", {
    className: wrenchHeaderCellAddon
  }, interactiveAddon)));
};
var useStyles$1 = createMemoStyles(function (theme) {
  var palette = theme.palette;
  var shadowStyle = {
    boxShadow: "0 -1px 0 ".concat(palette.border.muted, ", 0px -2px 4px rgba(0, 0, 0, 0.14)")
  };
  var removeShadowStyle = {
    boxShadow: "0 -1px 0 ".concat(palette.border.muted)
  };
  var tableFooterStyles = {
    wrenchTableFooter: function (_a) {
      var sticky = _a.sticky,
        showFooterShadow = _a.showFooterShadow,
        shadow = _a.shadow;
      return __assign({
        position: sticky ? 'sticky' : null,
        bottom: sticky ? 0 : null,
        zIndex: sticky ? 1 : null,
        backgroundColor: sticky ? palette.background.light : null,
        transition: 'all 0.3s'
      }, sticky && shadow && (showFooterShadow ? shadowStyle : removeShadowStyle));
    }
  };
  return tableFooterStyles;
});
var TableFooter = function (_a) {
  var children = _a.children,
    _b = _a.sticky,
    sticky = _b === void 0 ? false : _b,
    _c = _a.shadow,
    shadow = _c === void 0 ? true : _c,
    rest = __rest(_a, ["children", "sticky", "shadow"]);
  var showFooterShadow = useContext(TableContext).showFooterShadow;
  var wrenchTableFooter = useStyles$1({
    sticky: sticky,
    showFooterShadow: showFooterShadow,
    shadow: shadow
  }).wrenchTableFooter;
  return React.createElement("tfoot", __assign({
    className: wrenchTableFooter
  }, safeRest(rest)), children);
};
var useStyles = createMemoStyles(function (theme) {
  var tableStyles = {
    wrenchTableContainer: function (_a) {
      var xScroll = _a.xScroll,
        maxHeight = _a.maxHeight;
      return __assign(__assign({}, defaultBaseline), {
        overflowX: xScroll ? 'auto' : null,
        maxHeight: maxHeight !== null && maxHeight !== void 0 ? maxHeight : null,
        overflowY: maxHeight ? 'auto' : null,
        position: 'relative'
      });
    },
    wrenchTable: function () {
      return __assign(__assign({}, defaultFonts(theme)), {
        width: '100%',
        borderCollapse: 'collapse',
        position: 'relative'
      });
    }
  };
  return tableStyles;
});
function useStickyShadow(_a) {
  var containerRef = _a.containerRef,
    tableObserverRef = _a.tableObserverRef,
    showStickyShadows = _a.showStickyShadows;
  var _b = useState(false),
    showShadow = _b[0],
    setShowShadow = _b[1];
  var observerRef = useRef(null);
  useEffect(function () {
    if (showStickyShadows) {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
      if (window.IntersectionObserver) {
        observerRef.current = new window.IntersectionObserver(function (_a) {
          var entry = _a[0];
          if (entry.isIntersecting) {
            setShowShadow(false);
          } else {
            setShowShadow(true);
          }
        }, {
          root: containerRef.current,
          rootMargin: '0px',
          threshold: 0.1
        });
        if (tableObserverRef.current) {
          observerRef.current.observe(tableObserverRef.current);
        }
      }
    }
    return function () {
      var _a;
      return (_a = observerRef.current) === null || _a === void 0 ? void 0 : _a.disconnect();
    };
  }, [tableObserverRef, containerRef, showStickyShadows]);
  return showStickyShadows && showShadow;
}
var Table = function (_a) {
  var _b, _c, _d;
  var children = _a.children,
    _e = _a.xScroll,
    xScroll = _e === void 0 ? false : _e,
    _f = _a.density,
    density = _f === void 0 ? 'md' : _f,
    maxHeight = _a.maxHeight,
    _g = _a.showStickyShadows,
    showStickyShadows = _g === void 0 ? false : _g,
    rest = __rest(_a, ["children", "xScroll", "density", "maxHeight", "showStickyShadows"]);
  /* refs needed for the IntersectionObserver instances for each of the shadows */
  var containerRef = useRef(null);
  var tableRef = useRef(null);
  var tableHeaderRef = useRef(null);
  var tableFooterRef = useRef(null);
  var tableColumnRef = useRef(null);
  var tableColumnRightRef = useRef(null);
  /* call custom hook to set IntersectionObserver for each shadow */
  var showHeaderShadow = useStickyShadow({
    containerRef: containerRef,
    tableObserverRef: tableHeaderRef,
    showStickyShadows: showStickyShadows
  });
  var showFooterShadow = useStickyShadow({
    containerRef: containerRef,
    tableObserverRef: tableFooterRef,
    showStickyShadows: showStickyShadows
  });
  var showColumnShadow = useStickyShadow({
    containerRef: containerRef,
    tableObserverRef: tableColumnRef,
    showStickyShadows: showStickyShadows
  });
  var showColumnRightShadow = useStickyShadow({
    containerRef: containerRef,
    tableObserverRef: tableColumnRightRef,
    showStickyShadows: showStickyShadows
  });
  var _h = useStyles({
      xScroll: xScroll,
      maxHeight: maxHeight
    }),
    wrenchTableContainer = _h.wrenchTableContainer,
    wrenchTable = _h.wrenchTable;
  /* use table context.ts so table children can show the shadow with CSS */
  var value = useMemo(function () {
    return {
      density: density,
      showHeaderShadow: showHeaderShadow,
      showFooterShadow: showFooterShadow,
      showColumnShadow: showColumnShadow,
      showColumnRightShadow: showColumnRightShadow
    };
  }, [density, showHeaderShadow, showFooterShadow, showColumnShadow, showColumnRightShadow]);
  /* size of table ref needed to set the size of the shadow IntersectionObserver containers */
  var tableWidth = ((_c = (_b = tableRef.current) === null || _b === void 0 ? void 0 : _b.clientWidth) !== null && _c !== void 0 ? _c : 0) - 1;
  var tableHeight = (_d = tableRef.current) === null || _d === void 0 ? void 0 : _d.clientHeight;
  var tableContainer = React.createElement("table", __assign({
    ref: showStickyShadows ? tableRef : null,
    className: wrenchTable
  }, safeRest(rest)), children);
  var tableWithShadows = React.createElement(React.Fragment, null, React.createElement("div", {
    ref: tableHeaderRef,
    style: {
      position: 'absolute',
      left: 0,
      width: tableWidth
    }
  }), React.createElement("div", {
    ref: tableColumnRef,
    style: {
      position: 'absolute',
      left: 0,
      height: tableHeight
    }
  }), tableContainer, React.createElement("div", {
    ref: tableColumnRightRef,
    style: {
      position: 'absolute',
      top: 0,
      left: tableWidth,
      height: tableHeight
    }
  }), React.createElement("div", {
    ref: tableFooterRef,
    style: {
      position: 'absolute',
      left: 0,
      width: tableWidth
    }
  }));
  return React.createElement(TableContext.Provider, {
    value: value
  }, React.createElement("div", {
    className: wrenchTableContainer,
    ref: showStickyShadows ? containerRef : null
  }, showStickyShadows ? tableWithShadows : tableContainer));
};
Table.Header = TableHeader;
Table.Body = TableBody;
Table.Row = TableRow;
Table.Cell = TableCell;
Table.HeaderCell = TableHeaderCell;
Table.Footer = TableFooter;
var STICKY_SIDES = ['left', 'right'];
var DENSITIES = ['sm', 'md', 'lg'];
var ALIGNMENTS = ['left', 'center', 'right'];
var BACKGROUND_COLORS = __spreadArray(__spreadArray([], BACKGROUND_SEMANTIC_COLORS, true), BACKGROUND_COLORS$1, true);
var CELL_BACKGROUND_COLOR = ['main', 'light'];
export { ALIGNMENTS, BACKGROUND_COLORS, CELL_BACKGROUND_COLOR, DENSITIES, STICKY_SIDES, Table };